// Nivåer og risiko.
// Testet (5 år, 60 aksjer): støtte/motstand, 50/200-dagers snitt, runde tall og volum-POC holder IKKE oftere enn
// tilfeldige prisnivåer (≈50 % snur, ≈50 % brytes). I AI-aksjene brytes 52-ukers toppen oftere enn den holder.
// Derfor brukes nivåene her som referanse (hvor mange følger med), mens tapsgrense og mål bygger på
// volatilitet (ATR), trend (50-dagers snitt) og opsjonenes forventede bevegelse.

export function calcVolumeProfile(highs, lows, volumes, n, numBuckets = 80) {
  const hSlice = highs.slice(-n), lSlice = lows.slice(-n), vSlice = volumes.slice(-n);
  const validH = hSlice.filter(Boolean), validL = lSlice.filter(Boolean);
  if (!validH.length || !validL.length) return null;
  const lo = Math.min(...validL), hi = Math.max(...validH);
  if (!isFinite(lo) || !isFinite(hi) || hi <= lo) return null;
  const bucketSize = (hi - lo) / numBuckets;
  const buckets = new Array(numBuckets).fill(0);
  for (let i = 0; i < hSlice.length; i++) {
    if (!hSlice[i] || !lSlice[i]) continue;
    const vol = vSlice[i] || 0;
    if (!vol) continue;
    const bLo = Math.max(0, Math.floor((lSlice[i] - lo) / bucketSize));
    const bHi = Math.min(numBuckets - 1, Math.floor((hSlice[i] - lo) / bucketSize));
    const span = Math.max(1, bHi - bLo + 1);
    const vpb = vol / span;
    for (let b = bLo; b <= bHi; b++) buckets[b] += vpb;
  }
  const maxVol = Math.max(...buckets);
  if (!maxVol) return null;
  const pocIdx = buckets.indexOf(maxVol);
  const poc = lo + (pocIdx + 0.5) * bucketSize;
  const totalVol = buckets.reduce((s, v) => s + v, 0);
  let vaVol = buckets[pocIdx], vaLo = pocIdx, vaHi = pocIdx;
  while (vaVol < totalVol * 0.7) {
    const nL = vaLo > 0 ? buckets[vaLo - 1] : 0;
    const nH = vaHi < numBuckets - 1 ? buckets[vaHi + 1] : 0;
    if (!nL && !nH) break;
    if (nH >= nL && vaHi < numBuckets - 1) { vaHi++; vaVol += nH; }
    else if (vaLo > 0) { vaLo--; vaVol += nL; }
    else { vaHi++; vaVol += nH; }
  }
  return {
    buckets: buckets.map((vol, i) => ({ price: lo + (i + 0.5) * bucketSize, vol })),
    poc, val: lo + vaLo * bucketSize, vah: lo + (vaHi + 1) * bucketSize,
    maxVol, lo, hi, bucketSize,
  };
}

export function calcATR(highs, lows, closes, period = 14) {
  const tr = [];
  for (let i = 1; i < closes.length; i++) {
    const h = highs[i], l = lows[i], pc = closes[i - 1];
    if (h == null || l == null || pc == null) continue;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  const r = tr.slice(-period);
  return r.length ? r.reduce((a, b) => a + b, 0) / r.length : null;
}

const roundStep = p => (p >= 500 ? 50 : p >= 100 ? 10 : p >= 20 ? 5 : 1);

// Forankret VWAP = snittprisen (volumvektet) alle har betalt siden en hendelse.
// Faste ankere over siste år, så linjene ikke flytter seg når du bytter periode i grafen:
//   low  – årets bunn: «har kjøperne siden bunnen tjent penger?» Testet: AI-aksjer UNDER denne gjorde det
//          1,5 prosentpoeng svakere neste 20 dager (+1,5 % mot +3,0 % mot S&P). De andre ga ingen målbar forskjell.
//   high – årets topp: snittprisen for alle som har kjøpt siden toppen.
//   gap  – siste store kurshopp (største åpningsgap siste 3 mnd), ofte en rapport.
export function anchoredVwaps({ closes, highs, lows, volumes, opens, timestamps }) {
  const n = closes.length;
  if (n < 30) return null;
  const from = Math.max(0, n - 252);
  let lo = from, hi = from;
  for (let i = from; i < n; i++) {
    if (lows[i] != null && (lows[lo] == null || lows[i] < lows[lo])) lo = i;
    if (highs[i] != null && (highs[hi] == null || highs[i] > highs[hi])) hi = i;
  }
  let gap = null, gmax = 0.03;   // minst 3 % gap for å regnes som en hendelse
  if (opens?.length) for (let i = Math.max(1, n - 63); i < n; i++) {
    if (opens[i] == null || closes[i - 1] == null) continue;
    const g = Math.abs(opens[i] / closes[i - 1] - 1);
    if (g > gmax) { gmax = g; gap = i; }
  }
  const series = a => {
    let pv = 0, v = 0;
    return closes.map((c, i) => {
      if (i < a || c == null) return null;
      const tp = ((highs[i] ?? c) + (lows[i] ?? c) + c) / 3;
      pv += tp * (volumes[i] || 0); v += volumes[i] || 0;
      return v > 0 ? pv / v : null;
    });
  };
  const last = [...closes].reverse().find(v => v != null);
  const make = (key, idx, label, color, meaning) => {
    if (idx == null || idx >= n - 2) return null;
    const s = series(idx), val = [...s].reverse().find(v => v != null);
    const date = timestamps?.[idx] ? new Date(timestamps[idx] * 1000).toISOString().slice(0, 10) : null;
    return { key, idx, date, label, color, meaning, series: s, value: val, pct: val ? (last / val - 1) * 100 : null };
  };
  return [
    make("low", lo, "VWAP fra årets bunn", "#10b981", "Snittprisen alle har betalt siden bunnen."),
    make("high", hi, "VWAP fra årets topp", "#f43f5e", "Snittprisen alle har betalt siden toppen."),
    gap != null ? make("gap", gap, `VWAP fra kurshopp (${(gmax * 100).toFixed(0)} %)`, "#fb923c", "Snittprisen siden siste store hendelse, ofte en rapport.") : null,
  ].filter(Boolean);
}

// td: { closes, highs, lows, volumes } (dagsdata, gjerne 1 år). opt: { callWall, putWall, zeroGamma } (valgfri).
// Returnerer nivåsoner (sammenfallende nivåer innen ~0,4 ATR slått sammen), sortert etter avstand.
export function computeLevels(td, opt = {}) {
  const idx = td.closes.map((c, i) => i).filter(i => td.closes[i] != null && td.highs[i] != null && td.lows[i] != null);
  const c = idx.map(i => td.closes[i]), h = idx.map(i => td.highs[i]), l = idx.map(i => td.lows[i]), v = idx.map(i => td.volumes?.[i] || 0);
  const n = c.length;
  if (n < 30) return null;
  const price = c[n - 1], atr = calcATR(h, l, c) || price * 0.02;
  const ma = k => (n >= k ? c.slice(-k).reduce((a, b) => a + b, 0) / k : null);
  const raw = [];
  const push = (p, type, label) => { if (p && isFinite(p) && Math.abs(p - price) / price < 0.3) raw.push({ p, type, label }); };

  push(ma(50), "ma50", "50-dagers snitt");
  push(ma(200), "ma200", "200-dagers snitt");
  const yr = Math.min(n, 252);
  push(Math.max(...h.slice(-yr)), "hi52", "52-ukers topp");
  push(Math.min(...l.slice(-yr)), "lo52", "52-ukers bunn");
  const vp = calcVolumeProfile(h, l, v, Math.min(n, 126), 60);
  if (vp) { push(vp.poc, "poc", "Volum-POC (6 mnd)"); push(vp.vah, "vah", "Toppen av verdiområdet"); push(vp.val, "val", "Bunnen av verdiområdet"); }
  // større svingtopper og -bunner siste år (±10 dager)
  for (let i = Math.max(10, n - 252); i < n - 10; i++) {
    let mx = true, mn = true;
    for (let j = i - 10; j <= i + 10; j++) if (j !== i) { if (h[j] >= h[i]) mx = false; if (l[j] <= l[i]) mn = false; }
    if (mx) push(h[i], "swing", "Tidligere topp");
    if (mn) push(l[i], "swing", "Tidligere bunn");
  }
  const s = roundStep(price);
  for (let k = -2; k <= 2; k++) push((Math.round(price / s) + k) * s, "round", "Rundt tall");
  const vw = anchoredVwaps({ closes: c, highs: h, lows: l, volumes: v, opens: td.opens ? idx.map(i => td.opens[i]) : null });
  const vwLow = vw?.find(x => x.key === "low");
  if (vwLow?.value) push(vwLow.value, "vwaplow", "VWAP fra årets bunn");
  if (opt.callWall) push(opt.callWall, "callwall", "Call-vegg (opsjoner)");
  if (opt.putWall) push(opt.putWall, "putwall", "Put-vegg (opsjoner)");
  if (opt.zeroGamma) push(opt.zeroGamma, "zgamma", "Zero gamma");

  // slå sammen nivåer som ligger nær hverandre til soner
  raw.sort((a, b) => a.p - b.p);
  const zones = [];
  for (const r of raw) {
    const z = zones[zones.length - 1];
    if (z && r.p - z.hi <= atr * 0.4) { z.items.push(r); z.hi = r.p; }
    else zones.push({ lo: r.p, hi: r.p, items: [r] });
  }
  const WEIGHT = { ma50: 1.5, ma200: 2, hi52: 2, lo52: 2, poc: 1.5, vah: 1, val: 1, swing: 1, round: 0.5, callwall: 1.5, putwall: 1.5, zgamma: 1, vwaplow: 1.5 };
  const out = zones.map(z => {
    const types = [...new Set(z.items.map(i => i.type))];
    const labels = [...new Set(z.items.map(i => i.label))];
    const p = z.items.reduce((a, i) => a + i.p, 0) / z.items.length;
    return { p, lo: z.lo, hi: z.hi, types, labels, strength: types.reduce((a, t) => a + (WEIGHT[t] || 1), 0), dist: (p / price - 1) * 100 };
  }).filter(z => !(z.types.length === 1 && z.types[0] === "round" && Math.abs(z.dist) > 3));
  return {
    price, atr, atrPct: atr / price * 100, ma50: ma(50), vwapLow: vwLow?.value ?? null,
    resistances: out.filter(z => z.p > price * 1.002).sort((a, b) => a.p - b.p),
    supports: out.filter(z => z.p < price * 0.998).sort((a, b) => b.p - a.p),
  };
}

// Handelsplan for kjøp nå: tapsgrense og mål ut fra volatilitet og trend, størrelse ut fra risiko.
export function tradePlan(lv, { iv30 = null, account = null, riskPct = 2 } = {}) {
  if (!lv) return null;
  const { price, atr, ma50 } = lv;
  // Tapsgrense: under 50-dagers snitt hvis det ligger rimelig nær (trendbrudd = salgsregel), ellers 2 × ATR.
  // Aldri nærmere enn 1,5 × ATR, ellers blir du stoppet ut av vanlig dagsstøy.
  let stop = price - 2 * atr, stopWhy = "2 × ATR under kursen (aksjens normale svingninger)";
  if (ma50 && ma50 < price && price - ma50 <= 3 * atr) {
    stop = ma50 - 0.5 * atr; stopWhy = "rett under 50-dagers snitt (trendbrudd)";
    if (price - stop < 1.5 * atr) { stop = price - 1.5 * atr; stopWhy = "under 50-dagers snitt, men minst 1,5 × ATR unna (vanlig dagsstøy)"; }
  }
  // Mål: opsjonenes forventede 1-månedsbevegelse, ellers 3 × ATR
  const move = iv30 ? price * iv30 * Math.sqrt(21 / 252) : 3 * atr;
  const target = price + move, targetWhy = iv30 ? `forventet 1-månedsbevegelse fra opsjonene (IV30 ${(iv30 * 100).toFixed(0)} %)` : "3 × ATR";
  const risk = price - stop, reward = target - price;
  const shares = account && risk > 0 ? Math.floor((account * riskPct / 100) / risk) : null;
  const firstRes = lv.resistances[0];
  return {
    entry: price, stop, stopWhy, target, targetWhy, riskPct: risk / price * 100, rewardPct: reward / price * 100, rr: reward / risk,
    shares, maxLoss: account ? account * riskPct / 100 : null,
    roadblock: firstRes && firstRes.p < target ? firstRes : null,
  };
}
